import { Button, FormLabel, HStack, Input, Select, Stack } from '@chakra-ui/react'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import type { AdditionBicycle, OfferProperty, OfferPropertyAddition, PackageItemModel } from 'BrokerTsLib/src/dtos'
import { BikeKinds } from 'BrokerTsLib/src/dtos'
import { Insurer } from 'BrokerTsLib/src/enums'
import type { UseFieldArrayRemove } from 'BrokerTsLib/src/rhf'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'
import { Add } from '../../../../../icons'

type BicycleProps = {
  offer: OfferProperty
  addon: PackageItemModel
}

const getDefaultItem = (addon: PackageItemModel) => {
  const defaultVariant = addon.variants.find(x => x.isDefault) ?? addon.variants[0]

  return {
    bikeType: 'Bike',
    packageItemId: addon.packageItemId,
    variantId: defaultVariant?.id,
    limit: defaultVariant?.defaultLimit?.toString(),
    riskId: addon.riskId,
  } as AdditionBicycle
}

function Bicycle({
  offer,
  addon
}: BicycleProps) {

  const {
    control,
    setValue
  } = useFormContext<OfferPropertyAddition>()

  const name = 'bicycles'

  const { append, fields, remove } = useFieldArray({
    control: control,
    name: name
  })

  // vychozi nastaveni hodnot na OfferPropertyAddition z OfferPart
  useEffect(() => {
    fields.forEach((item, index) => {
      const part = offer.parts.find(x => x.partId == item.offerPartId)
      if (!part) return

      setValue(`${name}.${index}.riskId`, part.packageItem.riskId)
      setValue(`${name}.${index}.limit`, part.limit?.toString())
      setValue(`${name}.${index}.variantId`, part.packageItemVariantId)
      setValue(`${name}.${index}.offerPartId`, part.partId)
      setValue(`${name}.${index}.packageItemId`, part.packageItem.packageItemId)
    })

    if (fields.length == 0)
      append(getDefaultItem(addon))
  }, [])

  return (
    <HStack gap={8}>
      {fields?.map((item, index) => {
        return <BicycleItem key={`${item.offerPartId}_${index}`} addon={addon} index={index} item={item} remove={remove} />
      })}

      {offer.insurerId == Insurer.Direct && fields.length < 4 &&
                <Stack>
                  <Button
                    variant='outline'
                    onClick={() => append(getDefaultItem(addon))}
                  >
                    <Add color="secondary.500" />
                    Další
                  </Button>
                </Stack>
      }
    </HStack >
  )
}

export default Bicycle

type BicycleItemProps = {
  item: AdditionBicycle,
  index: number,
  addon: PackageItemModel,
  remove: UseFieldArrayRemove
}

const BicycleItem = ({
  item,
  index,
  addon,
  remove
}: BicycleItemProps) => {

  const name = 'bicycles'

  const {
    register,
    setValue,
    watch
  } = useFormContext<OfferPropertyAddition>()

  const handleChangeVariant = (limit: number, variantId?: number) => {
    setValue(`${name}.${index}.limit`, limit?.toString())
    setValue(`${name}.${index}.variantId`, variantId)
  }

  const limit = watch(`${name}.${index}.limit`)

  return (
    <Stack key={`${item.offerPartId}_${index}`} alignSelf='start' minW='120px'>
      <FormLabel alignSelf='center'>{index + 1}. kolo</FormLabel>

      <Select
        size='sm'
        sx={{ width: '100%' }}
        {...register(`${name}.${index}.limit`)}
        onChange={(e) => {
          const dataset = e.target.options[e.target.selectedIndex].dataset
          handleChangeVariant(parseInt(e.target.value), parseInt(dataset.id!))
        }}
      >
        {addon.variants.map(x => (
          <option key={`${x.id}_${x.defaultLimit}`} value={x.defaultLimit?.toString()} data-id={x.id}>
            {priceFormat(x.defaultLimit)} {x.isDefault ? '(výchozí)' : ''}
          </option>
        ))}
      </Select>

      {limit && parseInt(limit) > 10000 &&
                <>
                  <Select
                    size='sm'
                    sx={{
                      width: '100%'
                    }}
                    {...register(`${name}.${index}.bikeType`)}
                  >
                    {Object.keys(BikeKinds).map(key => {
                      const value = BikeKinds[key as keyof typeof BikeKinds]
                      return (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      )
                    })}
                  </Select>

                  <Input
                    size='sm'
                    placeholder='Značka'
                    {...register(`${name}.${index}.brand`)} />

                  <Input
                    size='sm'
                    placeholder='Id rámu'
                    {...register(`${name}.${index}.identification`)} />

                  <Input
                    size='sm'
                    placeholder='Popis'
                    {...register(`${name}.${index}.description`)} />
                </>
      }

      <Button
        size='sm'
        colorScheme='secondary'
        variant='ghost'
        onClick={() => remove(index)}
      >
        Odebrat
      </Button>
    </Stack>
  )
}
